import { WebappRoutes } from "@/shared/lib/webapp-routes"
import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"

import { useChat } from "@/features/chat"
import { advisorsList, AdvisorAdsCard, type Advisor } from "@/features/advisors"
import { bumpUsage } from "@/shared/lib/usage-stats"

import RunesDecoration from "@/assets/img/today/runes/runes_decoration.webp"
import RunesAds from "@/assets/img/today/runes/runes_ads.webp"
import RuneBack from "@/assets/img/today/runes/signs/sign_question_2x.webp"
import TapTip from "@/assets/svg/today/tap_tip.svg?react"

import { RUNES_DATA, findRune, type RuneData } from "../../config/runes-data"
import { useAstroToday } from "../../hooks/use-astro-today"
import { useOpenedScreens } from "../../hooks/use-opened-screens"
import { TodayTimer } from "../today-timer"
import { TodayLoader } from "../today-loader"
import { PersonalizedReports } from "../personalized-reports"
import { ADVISOR_PRESETS } from "../../config/advisor-presets"
import { RuneInfoModal } from "./rune-info-modal"

const SCREEN = "runes"

/** Rune-of-the-day category. Ported from Today/Runes. */
export function RunesCategory() {
  const navigate = useNavigate()
  const { setSelectedAdvisor, setPreSelectedChatMessage } = useChat()
  const { data, meta, loading } = useAstroToday(["runes"])
  const { isOpened, open } = useOpenedScreens()

  const [infoRune, setInfoRune] = useState<RuneData | null>(null)
  const advisor = advisorsList[14]
  const rune = findRune(data?.runes?.daily_rune)
  const opened = isOpened(SCREEN)

  const goToChat = (advisorToUse: Advisor, message?: string) => {
    if (message) setPreSelectedChatMessage(message)
    setSelectedAdvisor(advisorToUse)
    navigate({ to: WebappRoutes.READINGS })
  }

  const askFromModal = (chosen: Advisor) => {
    if (!infoRune) return
    const message = `I drew the ${infoRune.name} rune. Help me understand what this symbol means for my life right now.`
    setInfoRune(null)
    goToChat(chosen, message)
  }

  if (loading) return <TodayLoader />
  if (!rune) return <TodayLoader empty />

  return (
    <div className="w-full">
      <button
        type="button"
        onClick={
          opened
            ? undefined
            : () => {
                open(SCREEN)
                bumpUsage("runes")
              }
        }
        className="relative mx-4 mb-8 mt-10 flex items-center justify-center"
      >
        <img src={RunesDecoration} alt="" className="select-none" />
        <img
          src={opened ? rune.image : RuneBack}
          alt=""
          className="absolute w-[200px] animate-in fade-in duration-700"
        />
      </button>

      {!opened ? (
        <div className="mx-4 flex items-center justify-center gap-3 rounded-2xl bg-purple-light/5 px-4 py-3">
          <TapTip className="h-[42px] w-[35px] shrink-0" />
          <p className="font-body text-sm text-purple-light/70">
            Focus on your question and tap to reveal your personal{" "}
            <span className="font-medium text-purple-light">Rune of the Day</span>
          </p>
        </div>
      ) : (
        <div className="w-full animate-in fade-in duration-700">
          <div className="px-4">
            <div className="mb-6 flex items-start justify-between gap-6">
              <div>
                <h2 className="mb-2 font-display text-2xl font-medium text-purple-light">
                  {rune.name}
                </h2>
                <div className="flex flex-wrap items-center gap-[5px]">
                  {rune.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-[23px] border border-purple-light/20 px-3 py-1.5 font-body text-sm text-purple-light/70"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex h-[71px] flex-col items-center justify-center gap-1 rounded-2xl bg-purple-light/10 px-4">
                <span className="font-display text-2xl font-medium text-purple-light">
                  {rune.aett}
                </span>
                <span className="font-body text-[10px] font-medium text-purple-light/40">AETT</span>
              </div>
            </div>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: rune copy is trusted static content */}
            <p
              className="font-body text-base text-purple-light/70 [&_b]:font-medium [&_b]:text-purple-light"
              dangerouslySetInnerHTML={{ __html: rune.description }}
            />
          </div>

          <h3 className="mx-4 mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
            All runes
          </h3>
          <p className="mx-4 mb-4 font-body text-base text-purple-light/70">
            Uncover the meaning behind each symbol —{" "}
            <span className="font-medium text-white">scroll through and tap any rune</span> for its
            full interpretation.
          </p>
          <div className="flex items-center gap-4 overflow-x-auto px-4 [scrollbar-width:none]">
            {RUNES_DATA.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setInfoRune(item)}
                className="flex shrink-0 flex-col items-center gap-2"
              >
                <img src={item.smallImage} alt={item.name} className="h-32 w-24 min-w-24" />
                <span className="text-center font-body text-base text-purple-light/70">
                  {item.name}
                </span>
              </button>
            ))}
          </div>

          <div className="px-4">
            <h3 className="mb-2 mt-10 font-display text-2xl font-medium text-purple-light">
              This rune holds insight
            </h3>
            <p className="mb-6 font-body text-base text-purple-light/70">
              Direction, change, clarity — let a rune expert reveal what this symbol means for your
              life.
            </p>
            <AdvisorAdsCard
              advisor={advisor}
              message={`Welcome. You’ve drawn the ${rune.name}, a powerful sign of movement and alignment. Which part of your life would you like to explore deeper through its wisdom?`}
              presets={[...ADVISOR_PRESETS.runes]}
              onPresetClick={(preset) => goToChat(advisor, preset)}
            />
          </div>

          <PersonalizedReports />

          <h3 className="mb-2 mt-10 text-center font-display text-2xl font-medium text-purple-light">
            The runes are calling
          </h3>
          <div className="flex items-center justify-center py-2">
            <img src={RunesAds} alt="" className="select-none" />
          </div>
          <p className="mx-4 mb-4 text-center font-body text-base text-purple-light/70">
            These ancient symbols carry meaning meant{" "}
            <span className="font-medium text-white">just for you</span>. What message do they hold
            today? Speak with an advisor and uncover the truth.
          </p>
          <div className="mb-10 px-4">
            <button
              type="button"
              onClick={() => goToChat(advisor)}
              className="flex h-14 w-full items-center justify-center rounded-full bg-gradient-to-b from-peach to-purple-accent font-body text-base font-semibold text-white"
            >
              Ask an Advisor Now
            </button>
          </div>

          {meta && (
            <div className="mx-4 mt-6 flex items-center justify-between gap-4 border-t border-purple-light/20 pt-6">
              <p className="font-body text-base text-purple-light/70">
                Come back tomorrow to uncover your next rune
              </p>
              <TodayTimer target={meta.next_sync} />
            </div>
          )}
        </div>
      )}

      {infoRune && (
        <RuneInfoModal rune={infoRune} onClose={() => setInfoRune(null)} onAsk={askFromModal} />
      )}
    </div>
  )
}
